// @flow
import type { Node } from 'react';
import React, { useContext } from 'react';

type Props = {
  children: Node,
};

type contextType = {
  enableVoucherCode: boolean,
  enableResultDownload: boolean,
  enableDemo: boolean,
  fixedLanguage: string | null,
  logoLink: string,
  useDashboard: boolean,
  showAccountInfo: boolean,
  requiresAuth: boolean,
  showParamsResult: boolean,
  pdfReportOnResultsPage: boolean,
  enableCompanyRegistration: boolean,
  parameterPageType: 'default' | 'stepper',
  showAppBarLogo: boolean,
  loginOptions: { email?: boolean, microsoft?: { clientId: string, redirectUri: string } },
};

export const AppContext: React$Context<contextType> = React.createContext<contextType>({
  enableVoucherCode: false,
  enableResultDownload: false,
  enableDemo: false,
  fixedLanguage: null,
  logoLink: '',
  useDashboard: false,
  showAccountInfo: true,
  requiresAuth: true,
  contactButton: false,
  showParamsResult: true,
  pdfReportOnResultsPage: false,
  enableCompanyRegistration: false,
  parameterPageType: 'default',
  showAppBarLogo: true,
  loginOptions: { email: true },
});

/**
 * enableResultDownload: enable result download on dashboard
 * enableDemo: enable demo button for login
 * enableVoucherCode: enable voucher code field at registration
 * enableCompanyRegistration: enable company name field at registration
 * fixedLanguage: defines a fixed language, 'null' if non is given
 * logoLink: defines the link that is used on the logo in NewSimulation and ResultsPage
 * useDashboard: enables use of dashboard within the App, influences redirections on different pages
 * showAccountInfo: ahows/hides a button that leads to account infos (usually in footer), how is check out handled?
 * showParamsResult: shows/hides the parameter results page
 * pdfReportOnResultsPage: adds button in ResultsMenu for download of results pdf
 * parameterPageType: defines the appearance of the new simulation page for parameter input
 * loginOptions: defines which login options are avialble and defines additional information required
 */
const AppProvider = ({ children }: Props): Node => {
  const fixedLanguage = null;
  const enableDemo = false;
  const useDashboard = true;
  const enableResultDownload = true;
  const showParamsResult = true;
  const showAccountInfo = true;
  const requiresAuth = true;
  const enableVoucherCode = false;
  const enableCompanyRegistration = false;
  const pdfReportOnResultsPage = false;
  const logoLink = '';
  const parameterPageType = 'default';
  const showAppBarLogo = true;
  const loginOptions = { email: true };

  return (
    <AppContext.Provider
      value={{
        enableVoucherCode,
        enableCompanyRegistration,
        enableResultDownload,
        enableDemo,
        fixedLanguage,
        logoLink,
        useDashboard,
        showAccountInfo,
        requiresAuth,
        showParamsResult,
        pdfReportOnResultsPage,
        parameterPageType,
        showAppBarLogo,
        loginOptions,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): contextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within a AppProvider');
  }
  return context;
};

export default AppProvider;
